import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import {
  deselectMedia,
  StudioService,
  StudioVideo,
  StudioVideoShapeDescriptor,
} from '../../element-editor/store/studio';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { VideoPlayerService } from '../../element-editor/store/studio/video/video.service';
import { FileService } from '../../services/file/file.service';

import { v4 as uuidv4 } from 'uuid';

export interface VideoTimeRange {
  id?: string;
  from: number;
  to: number;
}

@Component({
  selector: 'nw-video-preview',
  templateUrl: './video-preview.component.html',
  styleUrls: ['./video-preview.component.scss'],
})
export class VideoPreviewComponent implements OnInit, OnDestroy, AfterViewInit {
  // @Input() video: StudioVideo | null = null;

  clips: VideoTimeRange[] = [];
  currentClipIndex = 0;
  _videoShape: StudioVideoShapeDescriptor | null = null;

  @Input()
  set videoShape(value: StudioVideoShapeDescriptor | null) {
    this._videoShape = value;
    this.clips = cloneDeep(this._videoShape?.clips || []);
  }

  get videoShape() {
    return this._videoShape;
  }

  @Output() timeRangeChange = new EventEmitter<VideoTimeRange[]>();

  @Output() close = new EventEmitter<void>();

  @ViewChild('videoElement', { static: false }) videoElement:
    | ElementRef<HTMLVideoElement>
    | undefined;
  @ViewChild('scrubVideo', { static: false }) scrubVideo:
    | ElementRef<HTMLVideoElement>
    | undefined;
  @ViewChild('timeline', { static: false }) timeline:
    | ElementRef<HTMLDivElement>
    | undefined;

  get video() {
    return this.videoShape
      ? this.studioService.videos[this.videoShape.videoId]
      : null;
  }

  // Timeline state
  currentTime = 0;
  // duration = 0;
  isPlaying = false;
  isDraggingStart = false;
  isDraggingEnd = false;
  isDraggingPlayhead = false;

  // Hover state for plus icon
  isHoveringTimeline = false;
  cursorPercentage = 0;
  isCursorOverClip = false;

  // Scrub video control
  private scrubSeekInFlight = false;
  private scrubPendingTime: number | null = null;

  // Video URL
  videoUrl = '';

  get duration() {
    return this.video?.duration || 0;
  }

  set duration(value: number) {
    // this.video = {
    //   ...this.video!,
    //   duration: value,
    // };
  }
  ngOnInit() {
    if (this.video) {
      this.videoUrl = this.getVideoUrl(this.video);
      console.log('video-url', this.videoUrl);
    }

    // console.log('ngOnInit > video', this.video); //
    // Initialize start and end times from StudioVideoShapeDescriptor if available
  }

  setScrubVideo(url: string) {
    const v = this.scrubVideo.nativeElement;

    // 1) stop & reset any previous load
    v.pause();
    v.removeAttribute('src');
    v.load();

    // 2) IMPORTANT: set crossOrigin FIRST
    v.crossOrigin = 'anonymous';

    // 3) then set src
    v.src = url;
    v.load();
  }

  setVideo(url: string) {
    const v = this.videoElement.nativeElement;

    // 1) stop & reset any previous load
    v.pause();
    v.removeAttribute('src');
    v.load();

    // 2) IMPORTANT: set crossOrigin FIRST
    v.crossOrigin = 'anonymous';

    // 3) then set src
    v.src = url;
    v.load();
  }

  getIsStartEndClose(index: number) {
    return this.clips[index].to - this.clips[index].from < 4;
  }

  constructor(
    private store: Store,
    private studioService: StudioService,

    private readonly videoService: VideoPlayerService,

    private readonly fileService: FileService,
  ) {}

  ngOnDestroy() {
    // Clean up
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.pause();
    }
  }

  timelineWidth = 0;
  unMovedStart;
  unMovedStartPercentage = 0;
  unMovedEnd;
  unMovedEndPercentage = 0;

  dragIsInProgress = false;
  ngAfterViewInit() {
    // -- //
    this.setScrubVideo(this.videoUrl);
    this.setVideo(this.videoUrl);

    const timeLine = this.timeline?.nativeElement;
    const { width } = timeLine?.getBoundingClientRect() || { width: 0 };
    console.log('timeline-width', width);
    this.timelineWidth = width;

    // Scrub to startTime when component opens
    const clip = this.clips[0];
    const startTime = clip?.from || 0;
    if (startTime > 0) {
      this.scrubTo(startTime);
      this.commitMainTo(startTime, false);
    }
  }

  getVideoUrl(video: StudioVideo): string {
    if (video.id) {
      return `https://private.cdn.inwedio.com/videos/${video.id}/video.${video.extension}`;
    }
    return '';
  }

  get thumbnailUrl() {
    return `https://private.cdn.inwedio.com/videos/${this.video?.id}/thumbnail.jpg`;
  }

  onVideoLoaded() {
    if (this.videoElement?.nativeElement) {
      this.duration = this.videoElement.nativeElement.duration;
      this.save();
    }
  }

  onTimeUpdate() {
    if (this.videoElement?.nativeElement) {
      this.currentTime = this.videoElement.nativeElement.currentTime;
      // -- //
      // Stop playback if we reach the end marker
      if (!this.currentClipIndex) {
        this.currentClipIndex = 0;
      }
      if (this.currentTime >= this.clips[this.currentClipIndex].to) {
        this.pause();
        // this.currentTime = this.endTime;
        // this.videoElement.nativeElement.currentTime = this.endTime;
      }
    }
  }

  play() {
    if (this.videoElement?.nativeElement) {
      if (!this.currentClipIndex) {
        this.currentClipIndex = 0;
      }
      if (this.currentTime >= this.clips[this.currentClipIndex].to) {
        this.currentTime = this.clips[this.currentClipIndex].from;
        this.videoElement.nativeElement.currentTime = this.currentTime;
      }
      this.videoElement.nativeElement.play();
      this.isPlaying = true;
    }
  }

  pause() {
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.pause();
      this.isPlaying = false;
    }
  }

  removeClip(cursorPercentage: number) {
    const timeInSecs = (cursorPercentage / 100) * this.duration;
    const clipIndex = this.clips.findIndex(
      clip => clip.from <= timeInSecs && clip.to >= timeInSecs,
    );
    if (clipIndex === -1) return;
    this.clips.splice(clipIndex, 1);
    this.save();
  }

  onClipClick(clipIndex: number) {
    this.currentClipIndex = clipIndex;
    this.currentTime = this.clips[clipIndex].from;
    // this.scrubTo(this.currentTime);
    // this.commitMainToRaf(this.currentTime);
  }

  togglePlayPause() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  rewind() {
    const newTime = Math.max(
      this.clips[this.currentClipIndex].from,
      this.currentTime - 5,
    );
    this.seekTo(newTime);
  }

  forward() {
    const newTime = Math.min(this.duration, this.currentTime + 5);
    this.seekTo(newTime);
  }

  seekTo(time: number) {
    if (this.videoElement?.nativeElement) {
      this.currentTime = Math.max(0, Math.min(time, this.duration));
      this.videoElement.nativeElement.currentTime = this.currentTime;
    }
  }

  // Scrub functionality
  private clamp(v: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, v));
  }

  private timeFromTimelineClientX(clientX: number): number {
    if (!this.timeline?.nativeElement) return 0;
    const el = this.timeline.nativeElement;
    const rect = el.getBoundingClientRect();
    const x = this.clamp(clientX - rect.left, 0, rect.width);
    const pct = rect.width ? x / rect.width : 0;
    return pct * this.duration;
  }

  /** Scrub quickly without disturbing the main player */
  private scrubTo(time: number) {
    if (!this.duration || !this.scrubVideo?.nativeElement) return;
    if (time == this.currentTime) return;

    const t = this.clamp(time, 0, this.duration);

    // latest-only: store last requested time
    this.scrubPendingTime = t;

    // if a seek is already happening, let it finish; we'll apply latest afterwards
    if (this.scrubSeekInFlight) return;

    this.scrubSeekInFlight = true;
    this.performSeek();
  }

  performSeek() {
    const onSeeked = () => {
      // The time setting of the video is completed
      this.scrubVideo.nativeElement.removeEventListener('seeked', onSeeked);

      // If more scrubs came in while seeking, immediately apply latest
      if (this.scrubPendingTime != null) {
        this.performSeek();
      } else {
        this.scrubSeekInFlight = false;
      }
    };

    this.scrubVideo.nativeElement.addEventListener('seeked', onSeeked);
    this.scrubVideo.nativeElement.currentTime = this.scrubPendingTime;
    this.scrubPendingTime = null;
  }

  /** Commit to the visible video (what the user actually watches) */
  private commitMainTo(time: number, keepPlaying: boolean) {
    // console.log('commitMainTo', time); //
    if (!this.videoElement?.nativeElement) return;
    const main = this.videoElement.nativeElement;
    const t = this.clamp(time, 0, this.duration);
    const wasPlaying = !main.paused && !main.ended;

    main.currentTime = t;

    if (keepPlaying || wasPlaying) {
      // if browser blocks autoplay, it'll just fail silently
      main.play().catch(() => {});
    }
  }

  // Timeline interaction handlers
  onTimelineClick(event: MouseEvent) {
    if (!this.duration) return;

    if (this.disableClick) {
      this.disableClick = false;
      return;
    }

    if (this.showPlusIcon) {
      this.addClip();
      return;
    }

    if (this.showRemoveIcon) {
      this.removeClip(this.cursorPercentage);
      return;
    }

    const t = this.timeFromTimelineClientX(event.clientX);

    console.log('onTimelineClick', t); //
    this.currentTime = t;
    // Scrub first (for "feel"), then commit
    this.scrubTo(t);
    this.commitMainTo(t, this.isPlaying);
  }

  dragStart(dx: number, clipIndex: number) {
    this.dragIsInProgress = true;
    this.currentClipIndex = clipIndex;
    console.log('dragStart', dx); //
    if (this.unMovedStart == undefined) {
      const start = this.clips[clipIndex].from;
      const end = this.clips[clipIndex].to;
      this.unMovedStartPercentage = this.getPercentage(start) / 100;
      this.unMovedEndPercentage = this.getPercentage(end) / 100;
      this.unMovedStart = this.unMovedStartPercentage * this.timelineWidth;
      console.log('unMovedStart', this.unMovedStart); //
      console.log('unMovedStartPercentage', this.unMovedStartPercentage); //
      console.log('timelineWidth', this.timelineWidth); //
    }

    const nextY = this.unMovedStart + dx;
    const nextPercentage = Math.max(
      0,
      Math.min(this.unMovedEndPercentage, nextY / this.timelineWidth),
    );

    this.clips[clipIndex].from = nextPercentage * this.duration;
    this.currentTime = this.clips[clipIndex].from;
    // console.log('currentTime', this.currentTime); //
    // Scrub to the new start time
    this.scrubTo(this.currentTime);
    this.commitMainToRaf(this.currentTime);
  }

  endDragIntervalStart() {
    this.disableClick = true;
    this.unMovedStart = null;

    console.log('saveThumbnailOfVideo'); //
    this.fileService.saveThumbnailOfVideo(this.videoElement.nativeElement, {
      videoId: this.video.id,
      thumbnailId: this.clips[this.currentClipIndex].id,
      maxWidth: this.video.width,
    });

    this.save();
  }

  private rafSeekPending: number | null = null;
  private rafSeekTime: number | null = null;
  private commitMainToRaf(time: number) {
    this.rafSeekTime = time;
    if (this.rafSeekPending != null) return;

    this.rafSeekPending = requestAnimationFrame(() => {
      this.rafSeekPending = null;
      if (this.rafSeekTime != null) {
        this.commitMainTo(this.rafSeekTime, false);
      }
    });
  }

  dragIntervalEnd(dx: number, clipIndex: number) {
    // -- //
    this.dragIsInProgress = true;
    this.currentClipIndex = clipIndex;
    if (this.unMovedEnd == undefined) {
      const end = this.clips[clipIndex].to;
      const start = this.clips[clipIndex].from;
      this.unMovedStartPercentage = this.getPercentage(start) / 100;
      this.unMovedEndPercentage = this.getPercentage(end) / 100;
      this.unMovedEnd = this.unMovedEndPercentage * this.timelineWidth;
    }
    const nextX = this.unMovedEnd + dx;
    const nextPercentage = Math.max(
      this.unMovedStartPercentage,
      Math.min(1, nextX / this.timelineWidth),
    );

    this.clips[clipIndex].to = nextPercentage * this.video.duration;

    this.currentTime = Math.min(this.clips[clipIndex].to, this.duration);
    // console.log('endTime', this.endTime); //
    // Scrub to the new end time

    this.scrubTo(this.currentTime);
    this.commitMainToRaf(this.currentTime);
  }

  disableClick = false;
  endDragIntervalEnd() {
    const start = this.clips[this.currentClipIndex].from;
    this.scrubTo(start);
    this.commitMainToRaf(start);
    this.disableClick = true;
    this.unMovedEnd = null;
    this.save();
  }
  // Drag handlers for start marker
  onStartMarkerMouseDown(event: MouseEvent) {
    console.log('onStartMarkerMouseDown');
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingStart = true;
    this.pause();
  }

  private onMouseUp = () => {
    this.isDraggingStart = false;
    this.isDraggingEnd = false;
    this.isDraggingPlayhead = false;
    // document.removeEventListener('mousemove', this.onMouseMove);
    // document.removeEventListener('mouseup', this.onMouseUp);
  };

  private save() {
    this.timeRangeChange.emit(cloneDeep(this.clips));

    // Save to store
    if (this.videoShape) {
      // this.store.dispatch(
      //   updateStudioVideoShapeDescriptor({
      //     videoId: this.videoShape.mediaId,
      //     StudioVideoShapeDescriptorId: this.videoShape.id,
      //     updates: {
      //       from: this.startTime,
      //       to: this.endTime,
      //     },
      //   }),
      // );
    }
  }

  // Helper methods for template
  getPercentage(start: number): number {
    return (start / this.duration) * 100;
  }

  getPlayheadPercentage(): number {
    return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
  }

  onTimelineMouseEnter() {
    this.isHoveringTimeline = true;
  }

  onTimelineMouseLeave() {
    this.isHoveringTimeline = false;
  }

  onTimelineMouseMove(event: MouseEvent) {
    if (!this.timeline?.nativeElement) return;

    const rect = this.timeline.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    this.cursorPercentage = Math.max(0, Math.min(100, (x / rect.width) * 100));

    this.isCursorOverClip = this.clips.some(clip => {
      const clipStartPct = this.getPercentage(clip.from);
      const clipEndPct = this.getPercentage(clip.to);
      return (
        this.cursorPercentage >= clipStartPct &&
        this.cursorPercentage <= clipEndPct
      );
    });
  }

  get showPlusIcon(): boolean {
    if (this.dragIsInProgress) return false;
    return this.isHoveringTimeline && !this.isCursorOverClip;
  }

  get showRemoveIcon(): boolean {
    if (this.dragIsInProgress) return false;
    return this.isHoveringTimeline && this.isCursorOverClip;
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  addClip() {
    console.log('addClip', this.cursorPercentage); //
    const timeInSecs = (this.cursorPercentage / 100) * this.duration;
    this.clips.push({
      from: timeInSecs,
      to: timeInSecs + 3,
    });
    this.save();
  }

  onClose() {
    this.store.dispatch(deselectMedia());
  }
}
